import type { CSSProperties, FC, ReactNode } from "react";
import { useContext } from "react";

import { DEBUG_TIMELINE_QUERY_PARAM, TIMELINE_VIEW_QUERY_PARAM } from "components/constants";
import { HoverableToolbarContext } from "components/hover-widgets/HoverableToolbarProvider";
import { DownloadUploadOptions } from "components/menus/DownloadUploadOptions";
import { PageType } from "components/server-types";
import { t } from "i18n/i18n";
import { IconText } from "mds/components/IconText";
import type { MenuChangeEvent, MenuDirection, MenuInstance } from "mds/components/Menu";
import { Menu, MenuDivider, MenuItem } from "mds/components/Menu";
import { SquareIconButton } from "mds/components/buttons/SquareIconButton";
import {
  ClockIcon,
  DotMenuVerticalIcon,
  DuplicateCopyIcon,
  HelpBugIcon,
  TrashDeleteIcon,
} from "mds/icons";
import { useCapabilities } from "store/capability-hooks";
import { useAppDispatch, useAppSelector } from "store/index";
import { selectIsTimeline } from "store/selectors/view";
import { viewStateActions } from "store/slices/view";
import { useQueryParam, useQueryParamsActions } from "utils/routing";

// This is the base component for SideBarOptionsMenu.  It is used in the other components
// that extend it for different models ex: Course, Topic, Activity, Resource.
export type SidebarOptionsMenuProps = {
  styles?: CSSProperties;
  className?: string;
  direction?: MenuDirection;
  children?: ReactNode;
  instanceRef?: React.Ref<MenuInstance>;
  eventPrefix?: string;
  optionsMenuActions?: {
    onDelete?: (() => void) | (() => Promise<void>);
    isDeleteDisabled?: boolean;
    isDuplicateDisabled?: boolean;
    deleteTooltipText?: string;
    onDuplicate?: () => void;
    showDownloadUploadOptions?: boolean;
    isUploadDisabled?: boolean;
    showTimeline?: boolean;
    page?: PageType;
  };
  kind?: "ghost" | "secondary";
  size?: "xs" | "s";
};

export const SidebarOptionsMenu: FC<SidebarOptionsMenuProps> = ({
  styles,
  className,
  direction = "right",
  children = null,
  optionsMenuActions,
  instanceRef = null,
  eventPrefix,
  kind = "ghost",
  size = "s",
}) => {
  const MenuButton = (
    <SquareIconButton
      aria-label={t("common.options")}
      className={className}
      eventName={eventPrefix}
      kind={kind}
      size={size}
      onClick={(e: React.MouseEvent) => {
        e.preventDefault();
        e.stopPropagation();
      }}
    >
      <DotMenuVerticalIcon />
    </SquareIconButton>
  );

  const hoverableContext = useContext(HoverableToolbarContext);

  const onMenuChange = (event: MenuChangeEvent) => {
    hoverableContext?.setShouldStayOpen?.(event.open);
  };

  return (
    <Menu
      direction={direction}
      instanceRef={instanceRef}
      menuButton={MenuButton}
      menuClassName="min-w-[180px]"
      menuStyle={styles}
      onMenuChange={onMenuChange}
    >
      {children}
      {optionsMenuActions?.onDuplicate && (
        <MenuItem
          disabled={optionsMenuActions?.isDuplicateDisabled}
          eventName={eventPrefix ? `${eventPrefix}Duplicate` : undefined}
          onClick={optionsMenuActions.onDuplicate}
        >
          <IconText iconStart={<DuplicateCopyIcon />} text={t("sidebar_options_menu.duplicate")} />
        </MenuItem>
      )}
      {(optionsMenuActions?.showDownloadUploadOptions || optionsMenuActions?.showTimeline) &&
        optionsMenuActions.page && (
          <>
            <MenuDivider />
            {optionsMenuActions?.showDownloadUploadOptions && (
              <DownloadUploadOptions
                disabled={optionsMenuActions.isUploadDisabled}
                page={optionsMenuActions.page}
              />
            )}
            {optionsMenuActions?.showTimeline && <TimelineMenuItems />}
          </>
        )}
      {optionsMenuActions?.onDelete && (
        <>
          {Object.keys(optionsMenuActions).filter((key) => !key.toLowerCase().includes("delete"))
            .length > 1 && <MenuDivider />}
          {/* TODO: The disabled state for the Delete action should be greyed out instead of hoverable & red. */}
          <MenuItem
            disabled={optionsMenuActions.isDeleteDisabled}
            eventName={eventPrefix ? `${eventPrefix}Delete` : undefined}
            kind="destructive"
            title={optionsMenuActions.deleteTooltipText}
            onClick={optionsMenuActions.onDelete}
          >
            <IconText
              iconStart={
                <TrashDeleteIcon
                  className={
                    optionsMenuActions.isDeleteDisabled ? "text-black-tint-55" : "text-red"
                  }
                />
              }
              text={t("common.delete")}
            />
          </MenuItem>
        </>
      )}
    </Menu>
  );
};

const exitTimelineView = (
  dispatch: ReturnType<typeof useAppDispatch>,
  deleteQueryParams: ReturnType<typeof useQueryParamsActions>["deleteQueryParams"],
) => {
  dispatch(viewStateActions.setIsTimelineView(false));
  deleteQueryParams([TIMELINE_VIEW_QUERY_PARAM, DEBUG_TIMELINE_QUERY_PARAM]);
};

const TimelineMenuItems: FC = () => {
  const dispatch = useAppDispatch();
  const { canUseDebugFeatures } = useCapabilities();
  const { setQueryParams, deleteQueryParams } = useQueryParamsActions();
  const isTimelineView = useAppSelector(selectIsTimeline);
  const isDebugTimeline = useQueryParam(DEBUG_TIMELINE_QUERY_PARAM) === "1";

  if (isTimelineView) {
    const text = isDebugTimeline
      ? t("worksheet.timeline.hide_debug_menu_label")
      : t("worksheet.timeline.hide_menu_label");
    return (
      <MenuItem title={text} onClick={() => exitTimelineView(dispatch, deleteQueryParams)}>
        <IconText iconStart={isDebugTimeline ? <HelpBugIcon /> : <ClockIcon />} text={text} />
      </MenuItem>
    );
  }

  return (
    <>
      <MenuItem
        title={t("worksheet.timeline.show_menu_label")}
        onClick={() => dispatch(viewStateActions.setIsTimelineView(true))}
      >
        <IconText iconStart={<ClockIcon />} text={t("worksheet.timeline.show_menu_label")} />
      </MenuItem>

      {canUseDebugFeatures && (
        <MenuItem
          title={t("worksheet.timeline.show_debug_menu_label")}
          onClick={() => {
            dispatch(viewStateActions.setIsTimelineView(true));
            setQueryParams({
              [TIMELINE_VIEW_QUERY_PARAM]: "1",
              [DEBUG_TIMELINE_QUERY_PARAM]: "1",
            });
          }}
        >
          <IconText
            iconStart={<HelpBugIcon />}
            text={t("worksheet.timeline.show_debug_menu_label")}
          />
        </MenuItem>
      )}
    </>
  );
};
